import { type ComponentProps, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ComboboxInput, type ComboboxPresetBaseProps, ComboboxRoot, type ComboboxRootProps } from './combobox';
import {
  ComboboxPresetContent,
  type CreatableMarker,
  type CreatableProps,
  isCreatable,
  type QueryProps,
  useComboboxInputValue,
  useCreatableHelpers,
  useCreatableItemEquality,
} from './combobox-internals';

export type SingleComboboxProps<T> = ComboboxPresetBaseProps<T> &
  Pick<ComboboxRootProps<T, false>, 'isItemEqualToValue' | 'itemToStringLabel' | 'disabled'> &
  Omit<ComponentProps<typeof ComboboxInput>, 'value' | 'onValueChange' | 'children' | 'disabled'> &
  QueryProps &
  CreatableProps<T> & {
    /** The selected item, or `null` when nothing is selected. */
    value?: T | null;
    /** Fired with the newly selected item, or `null` when the selection is cleared. */
    onValueChange?: (value: T | null) => void;
    /** Whether a page is currently in-flight. Suppresses repeat `onLoadMore` fires while true, and drives the initial-fetch loading copy in the popup. */
    isLoading?: boolean;
    /** Whether more pages remain. When false, the tail probe is not rendered. */
    hasMore?: boolean;
    /** Fired when the user scrolls near the bottom of the loaded set. Detected via an IntersectionObserver on a 1px probe element after the rows. */
    onLoadMore?: () => void;
    /** Extra content rendered in the popup below the list, outside the filtered collection (e.g. `ComboboxActions`). */
    children?: ReactNode;
  };

export const SingleCombobox = <T,>({
  items,
  value,
  onValueChange,
  getKey,
  getLabel,
  isItemEqualToValue,
  itemToStringLabel,
  emptyMessage,
  disabled,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  query,
  onQueryChange,
  onCreate,
  renderCreateOption,
  children,
  readOnly,
  ...inputProps
}: SingleComboboxProps<T>) => {
  const isServerFilter = query !== undefined && onQueryChange !== undefined;
  const { typedQuery, handleInputValueChange, clearQuery } = useComboboxInputValue(
    isServerFilter ? onQueryChange : undefined
  );
  const [inFlightCreatable, setInFlightCreatable] = useState<T | null>(null);

  const { itemsForView, localFilter, stableItemToStringLabel } = useCreatableHelpers(
    items,
    getLabel,
    itemToStringLabel,
    onCreate,
    typedQuery ?? ''
  );

  const defaultIsItemEqualToValue = useCreatableItemEquality(getKey);
  const effectiveIsItemEqualToValue = isItemEqualToValue ?? defaultIsItemEqualToValue;

  const showLoadingCopy = isLoading && !items?.length;

  const handleValueChange = useCallback<NonNullable<ComboboxRootProps<T, false>['onValueChange']>>(
    async (next) => {
      if (next && isCreatable(next) && onCreate) {
        setInFlightCreatable(next);

        try {
          const created = await onCreate((next as unknown as CreatableMarker).__create);
          onValueChange?.(created);
          setInFlightCreatable(null);
          clearQuery();
        } catch {
          // Keep the creatable sticky so the user can edit the query and retry.
        }

        return;
      }

      setInFlightCreatable(null);
      onValueChange?.(next);
    },
    [onCreate, onValueChange, clearQuery]
  );

  const rootValue = inFlightCreatable ?? value ?? null;
  const selectedLabel = rootValue !== null ? stableItemToStringLabel(rootValue) : '';
  const selectedKey = rootValue !== null ? String(getKey(rootValue)) : null;

  const previousSelectedKeyRef = useRef(selectedKey);
  useEffect(() => {
    // Clear transient search text when controlled state restores a committed selection.
    if (selectedKey !== null && previousSelectedKeyRef.current !== selectedKey) {
      clearQuery();
    }

    previousSelectedKeyRef.current = selectedKey;
  }, [clearQuery, selectedKey]);

  return (
    <ComboboxRoot
      items={itemsForView}
      value={rootValue}
      onValueChange={handleValueChange}
      disabled={disabled}
      readOnly={readOnly}
      loopFocus={!hasMore}
      itemToStringLabel={stableItemToStringLabel}
      isItemEqualToValue={effectiveIsItemEqualToValue}
      inputValue={typedQuery ?? selectedLabel}
      onInputValueChange={(next, details) => {
        if (inFlightCreatable !== null) {
          setInFlightCreatable(null);
        }
        handleInputValueChange(next, details.reason);
      }}
      filter={isServerFilter ? null : localFilter}
    >
      <ComboboxInput disabled={disabled} readOnly={readOnly} {...inputProps} />
      <ComboboxPresetContent
        items={itemsForView}
        getKey={getKey}
        getLabel={getLabel}
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        renderCreateOption={renderCreateOption}
        isServerFilter={isServerFilter}
        emptyMessage={emptyMessage}
        showLoadingCopy={showLoadingCopy}
      >
        {children}
      </ComboboxPresetContent>
    </ComboboxRoot>
  );
};
